import {Fade} from '@material-ui/core';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {InactivityTracker} from 'Shared/components/InactivityTracker/InactivityTracker';
import {hasScope} from 'Shared/components/RequireScope';
import {Scope} from 'Shared/constants/Scope';
import {isAuthenticated} from 'Shared/utilities/Auth';
import {getUserClaims} from 'Shared/utilities/UserProfileUtilities';
import {LocalStorageKey, toLocalStorage} from 'Shared/utilities/localStorage';
import {UserTypeClaimKey, UserTypeClaims} from 'Users/models/Claims';
import {isSome} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {ReactNode, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Redirect, Route, useLocation} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import RefreshSpinner from './Core/components/RefreshSpinner';
import {initiateTokenRefresh} from './Core/ducks/refreshToken';
import {setRefreshReRouteOptions} from './Core/refreshTokenUtilityMethods';

const getUserRoles = () => {
  const claims = pipe(getUserClaims());
  let userRoles: string[] = [];
  if (claims && isSome(claims) && claims?.value?.userTypes?.length > 0) {
    claims?.value?.userTypes.map(
      UserTypeClaims.match({
        [UserTypeClaimKey.AgentSite]: agent => {
          let userHasScope = hasScope(agent.scopes);
          userHasScope(Scope.ImpersonateAgent) ? userRoles.push('VIP') : userRoles.push('Agent');
          if (agent.impersonatedAgentClaim) userRoles.push('Agent');
        },
        [UserTypeClaimKey.Quoting]: () => {
          userRoles.push('Quoting');
        },
        [UserTypeClaimKey.MyAccessPortal]: () => {
          userRoles.push('Admin');
        },
        default: () => '',
      })
    );
  }
  return userRoles.join(',');
};

type AuthorizedRoutesProps = {
  children: ReactNode;
};

const INACTIVITY_WARNING_TIME_MS = 810000; // should show warning at 13 minutes 30 seconds of inactivity
const INACTIVITY_LOGOUT_TIME_AFTER_WARNING_MS = 90000; // should logout after 1 minutes 30 seconds post warning message is displayed

const BUFFER_TO_SILENT_TOKEN_REFRESH_WHEN_ACTIVE = 60000;

const AuthorizedRoutes = ({children}: AuthorizedRoutesProps) => {
  const dispatch = useDispatch();

  const isUserAuthenticated = isAuthenticated();

  const location = useLocation();

  const {expiresAt} = useSelector((state: ReducerState) => state.refreshToken);

  const sessionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    //add roles to windows.roles
    //@ts-ignore
    window.UserRoles = getUserRoles();
  }, []);

  useEffect(() => {
    if (expiresAt && expiresAt > 0) {
      const futureExpiration = new Date(expiresAt).getTime() - BUFFER_TO_SILENT_TOKEN_REFRESH_WHEN_ACTIVE;
      const aLittleBeforeExpiration = futureExpiration - new Date().getTime();
      sessionTimeoutRef.current = setTimeout(continueSession, aLittleBeforeExpiration);
    }
  }, [expiresAt]);

  const continueSession = () => {
    setRefreshReRouteOptions(false);
    dispatch(initiateTokenRefresh());
    clearTimeout(sessionTimeoutRef.current);
  };

  useBreadcrumbs('Home', '/');

  useEffect(() => {
    if (!isUserAuthenticated) {
      const saveRedirectRoute = () => {
        const routeToSave = location.pathname;
        
        const routesToExclude = [CoreRouteConstants.DASHBOARD, CoreRouteConstants.LOGIN];
        const isValidRedirectRoute =
          typeof routeToSave === 'string' && routeToSave.trim() !== '' && !routesToExclude.includes(routeToSave);
        
        if (isValidRedirectRoute) {
          toLocalStorage(LocalStorageKey.RedirectRoute)(routeToSave)();
        }
      };

      saveRedirectRoute();
    }
  }, []);

  return (
    <Route
      path="/"
      render={() =>
        isUserAuthenticated ? (
          <Fade mountOnEnter unmountOnExit in={isUserAuthenticated} timeout={{enter: 0, exit: 400}}>
            <>
              <RefreshSpinner />
              <InactivityTracker
                inactivityWarningTime={INACTIVITY_WARNING_TIME_MS}
                logoutTimeAfterWarning={INACTIVITY_LOGOUT_TIME_AFTER_WARNING_MS}
              />
              {children}
            </>
          </Fade>
        ) : (
          <Redirect to={'/login'} />
        )
      }
    />
  );
};

export default AuthorizedRoutes;
